<script setup>
import { onMounted } from "vue";

import { useTimeoutFn } from "@vueuse/core";

import { useNotifyStore } from "@Store/overhauling/useNotifyStore";

import defaultNotify from "@Components/notify/defaultNotify";

const { REMOVE_NOTIFY } = useNotifyStore();

/**
 * The properties from "resources/js/components/notify/NotifyWrapper.vue"
 */
const props = defineProps({
  notify: {
    type: Object,
    default: () => defaultNotify(),
    required: true,
  },
});

const ICONS = {
  success: "fa-check-circle",
  error: "fa-times-circle",
  info: "fa-info-circle",
};

function closeNotify() {
  REMOVE_NOTIFY(props.notify.id);
}

const { start: beginRemovalTimer } = useTimeoutFn(() => closeNotify(), props.notify.duration);

onMounted(() => {
  // self-remove the notify based on duration
  beginRemovalTimer();
});
</script>

<template>
  <div
    role="alert"
    class="_notify"
    :class="[`notify notify--${props.notify.type}`, `_notify-${props.notify.type}`]"
    @click="closeNotify"
  >
    <div class="notify__header">
      <i
        class="notify__icon"
        :class="`fa-solid ${ICONS[props.notify.type]}`"
      />

      <span class="notify__title">
        <template v-if="$slots.title">
          <slot name="title" />
        </template>
        <template v-else>
          {{ props.notify.title }}
        </template>
      </span>

      <i class="notify__close fa fa-times" />
    </div>

    <div class="notify__content">
      <template v-if="$slots.content">
        <slot name="content" />
      </template>
      <template v-else>
        {{ props.notify.text }}
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;

$notify-max-width: 500px;

.notify {
  padding: 1rem;
  margin-bottom: spacing.$xxxxs;
  color: #ffffff;
  max-width: $notify-max-width;
  word-break: break-word;
  justify-self: flex-end;

  background: #0c4eac;
  border-left: 5px solid #44a4fc;

  &__header {
    display: flex;
    align-items: center;

    font-size: typography.$xs-font-size;
    margin-bottom: spacing.$xs-5;
  }
  &__title {
    font-weight: typography.$font-weight-semi-bold;
    margin-left: spacing.$xs-5;
  }
  &__close {
    color: rgba($color: #fff, $alpha: 0.25);
    font-size: typography.$base-font-size;
    cursor: pointer;
    padding-left: 1rem;
    margin-left: auto;
  }
  &__content {
    font-size: typography.$xs-font-size;
    padding-right: spacing.$s;
  }

  &--error {
    background: #923b3b;
    border-left-color: #e54d42;
  }
  &--success {
    background: #2e633d;
    border-left-color: #42a85f;
  }
}
</style>
